import type { PlanTier } from '@unipostudio/shared-types';
/**
 * The currencies a plan can be bought in.
 *
 * Every one of these is a price set for its market, not a conversion. India
 * keeps its own number because a rupee price converted from euro would sit far
 * above what the market bears. The Gulf gets SAR and AED because that is what
 * a buyer in Riyadh or Dubai expects to read, and because the riyal's peg
 * makes a converted price look like an afterthought.
 *
 * `WorkspaceCurrency` in the web app is a superset — it also formats GBP,
 * which is a display currency here, not yet a billing one.
 */
export type BillingCurrency = 'EUR' | 'USD' | 'SAR' | 'AED' | 'INR';
export const BILLING_CURRENCIES: readonly BillingCurrency[] = ['EUR', 'USD', 'SAR', 'AED', 'INR'];

export interface PlanPrice {
  /** Charged monthly, in major units — euro, dollars, riyals, dirhams, rupees. */
  monthly: number;
  /** Charged once a year, at least 20% below twelve months. */
  yearly: number;
}

/**
 * The plans that are sold. ADR-007 (docs/decisions/ADR-007-free-starter-gst-inclusive.md),
 * on top of ADR-006's account model: the plan belongs to the **account**,
 * brands sit under it. Two plans are sold, Pro and Agency. Every account
 * that has not paid is on the free Starter plan, forever; there is no trial
 * and no read-only lapse.
 */
export type SellablePlan = 'PRO' | 'AGENCY';

/**
 * The plans an account can be paying for: the two sold, plus Classic, which
 * is no longer sold. A Classic subscriber is grandfathered (ADR-007): they
 * keep Classic's entitlements and their billed amount until they change
 * plan, and a change goes to Pro or Agency at today's prices.
 */
export type PaidPlan = 'CLASSIC' | SellablePlan;

/** The free plan every account is on when it is not paying. */
export const FREE_PLAN = 'STARTER' as const;

/**
 * What each plan costs, per currency. Prices are set per market, never
 * converted at a rate — a rate moves and a price should not.
 *
 * Yearly is twelve months less 20%, rounded *down* to a whole unit.
 *
 * Rupee prices of Pro and Agency (and the Agency extra brand) **include**
 * 18% GST (owner's decision, ADR-007): the customer pays exactly ₹999 or
 * ₹2,999 and the invoice shows the GST inside it (`indiaGstSplit`). Every
 * other currency is VAT-exclusive, unchanged, with Paddle adding the tax.
 *
 * CLASSIC is kept only to bill and recognise grandfathered subscribers: it
 * is in no price list, and its rupee price is the old one, with GST added on
 * top (`inrPriceIncludesGst`).
 */
export const PLAN_PRICES = {
  CLASSIC: {
    EUR: { monthly: 9, yearly: 86 },
    USD: { monthly: 9, yearly: 86 },
    SAR: { monthly: 39, yearly: 374 },
    AED: { monthly: 39, yearly: 374 },
    INR: { monthly: 499, yearly: 4790 },
  },
  PRO: {
    EUR: { monthly: 29, yearly: 278 },
    USD: { monthly: 29, yearly: 278 },
    SAR: { monthly: 109, yearly: 1046 },
    AED: { monthly: 109, yearly: 1046 },
    INR: { monthly: 999, yearly: 9590 },
  },
  AGENCY: {
    EUR: { monthly: 49, yearly: 470 },
    USD: { monthly: 49, yearly: 470 },
    SAR: { monthly: 199, yearly: 1910 },
    AED: { monthly: 199, yearly: 1910 },
    INR: { monthly: 2999, yearly: 28790 },
  },
} as const satisfies Record<PaidPlan, Record<BillingCurrency, PlanPrice>>;

/** Brands an Agency subscription includes before each further brand is charged. */
export const AGENCY_INCLUDED_BRANDS = 5;

/**
 * What each brand above `AGENCY_INCLUDED_BRANDS` adds to an Agency
 * subscription, per brand. Charged on the same interval as the plan: a yearly
 * Agency pays the yearly extra-brand price. ADR-006, "Brands and Agency extras".
 * The rupee price is GST-inclusive, like the plan it is added to (ADR-007).
 */
export const AGENCY_EXTRA_BRAND_PRICE = {
  EUR: { monthly: 10, yearly: 96 },
  USD: { monthly: 10, yearly: 96 },
  SAR: { monthly: 39, yearly: 374 },
  AED: { monthly: 39, yearly: 374 },
  INR: { monthly: 599, yearly: 5750 },
} as const satisfies Record<BillingCurrency, PlanPrice>;

/**
 * The advertised annual saving.
 *
 * Each yearly price above is rounded *down* to a clean number, so the real
 * saving is always a little over this rather than a little under. Advertising
 * a discount the arithmetic does not support is the kind of thing a consumer
 * regulator takes an interest in.
 */
export const ANNUAL_DISCOUNT_PERCENT = 20;

/** Twelve months less ANNUAL_DISCOUNT_PERCENT, rounded down: how every yearly price is set. */
export function yearlyFromMonthly(monthly: number): number {
  return Math.floor((monthly * 12 * (100 - ANNUAL_DISCOUNT_PERCENT)) / 100);
}

/**
 * Subscription plan feature matrix.
 *
 * Allowances (posts, AI generations, AI replies, posters, storage) are one
 * pool **shared by every brand of the account**. Team seats are **per brand**.
 * `maxWorkspaces` is the brands the plan includes; Agency may go past it, one
 * paid extra brand at a time (see `brandLimitFor`).
 *
 * `STARTER` is the free plan, forever (ADR-007): the owner's original free
 * tier — 3 channels, 10 posts a month, 1 seat, no AI, a read-only inbox, 7
 * days of analytics, 500 MB, 1 brand. Features that tier never named (the
 * inbox agent, posters, X credits, the planner, ...) are off or zero.
 * `CLASSIC` is not sold; it is here for grandfathered subscribers.
 */
export const PLAN_FEATURES = {
  STARTER: {
    name: 'Starter',
    priceInr: 0,
    maxPlatforms: 3,
    postsPerMonth: 10,
    /** X bills every post; the free plan has no X credits (ADR-007). */
    xPostCredits: 0,
    teamSeats: 1,
    aiGenerations: 0,
    inboxAccess: 'read_only' as const,
    aiReplyDrafts: 0,
    aiRepliesAutoSent: 0,
    occasionPosters: 0,
    analyticsRetentionDays: 7,
    storageBytes: 500 * 1024 * 1024, // 500MB
    maxWorkspaces: 1,
    extraBrandsPurchasable: false,
    scheduling: false,
    templates: 'global_only' as const,
    approvalWorkflow: false,
    slaEngine: false,
    advancedFiltering: false,
    csvExport: false,
    activityLogDays: 0,
    clientViewerAccess: false,
    apiAccess: false,
    whiteLabel: false,
  },
  CLASSIC: {
    name: 'Classic',
    priceInr: PLAN_PRICES.CLASSIC.INR.monthly,
    maxPlatforms: 11,
    postsPerMonth: Infinity,
    /** Monthly X post credits (x-usage.ts): a post 1, a post with a link 13. */
    xPostCredits: 150,
    teamSeats: 3,
    aiGenerations: 100,
    inboxAccess: 'reply' as const,
    aiReplyDrafts: 300,
    aiRepliesAutoSent: 300,
    occasionPosters: Infinity,
    analyticsRetentionDays: 90,
    storageBytes: 5 * 1024 * 1024 * 1024, // 5GB
    maxWorkspaces: 1,
    extraBrandsPurchasable: false,
    scheduling: true,
    templates: 'personal' as const,
    approvalWorkflow: true,
    slaEngine: false,
    advancedFiltering: true,
    csvExport: true,
    activityLogDays: 30,
    clientViewerAccess: false,
    apiAccess: false,
    whiteLabel: false,
  },
  PRO: {
    name: 'Pro',
    priceInr: PLAN_PRICES.PRO.INR.monthly,
    maxPlatforms: 11,
    postsPerMonth: Infinity,
    xPostCredits: 450,
    teamSeats: 3,
    aiGenerations: 100,
    inboxAccess: 'reply' as const,
    aiReplyDrafts: 300,
    aiRepliesAutoSent: 300,
    occasionPosters: Infinity,
    analyticsRetentionDays: 90,
    storageBytes: 5 * 1024 * 1024 * 1024, // 5GB
    // Same features as Classic; the three brands are what Pro pays for.
    maxWorkspaces: 3,
    extraBrandsPurchasable: false,
    scheduling: true,
    templates: 'personal' as const,
    approvalWorkflow: true,
    slaEngine: false,
    advancedFiltering: true,
    csvExport: true,
    activityLogDays: 30,
    clientViewerAccess: false,
    apiAccess: false,
    whiteLabel: false,
  },
  AGENCY: {
    name: 'Agency',
    priceInr: PLAN_PRICES.AGENCY.INR.monthly,
    maxPlatforms: 11,
    postsPerMonth: Infinity,
    xPostCredits: 1500,
    teamSeats: 10,
    aiGenerations: Infinity,
    inboxAccess: 'full' as const,
    aiReplyDrafts: 1200,
    // Capped: unlimited replies on a €49 plan inverted at roughly 4,500 a
    // month. Anyone past it buys a top-up pack.
    aiRepliesAutoSent: 1200,
    occasionPosters: Infinity,
    analyticsRetentionDays: 365,
    storageBytes: 25 * 1024 * 1024 * 1024, // 25GB
    /** Included brands. More may be added, each at AGENCY_EXTRA_BRAND_PRICE. */
    maxWorkspaces: AGENCY_INCLUDED_BRANDS,
    extraBrandsPurchasable: true,
    scheduling: true,
    templates: 'shared' as const,
    approvalWorkflow: true,
    slaEngine: true,
    advancedFiltering: true,
    csvExport: true,
    activityLogDays: Infinity,
    clientViewerAccess: true,
    apiAccess: true,
    whiteLabel: true,
  },
} as const;

/** Every plan an account can be on: the free Starter, grandfathered Classic, Pro and Agency. */
export type PlanName = keyof typeof PLAN_FEATURES;

// PlanTier (shared-types, used by WorkspaceContext.plan) must name exactly
// these plans: a plan added here without it fails to compile.
type SamePlans<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never;
export const PLAN_TIER_MATCHES_PLAN_NAME: SamePlans<PlanName, PlanTier> = true;

/** What a stored subscription says, as far as deciding the plan goes. */
export interface SubscriptionState {
  plan: string | null | undefined;
  status?: string | null;
  currentPeriodEnd?: Date | string | null;
  /**
   * End of a trial. No trial is started any more (ADR-007); the column is
   * read only for a provider-side trial still running.
   */
  trialEndsAt?: Date | string | null;
}

/**
 * Subscription statuses under which the plan has been paid for, or is being
 * paid for right now.
 *
 * `past_due` and `pending` are in on purpose: a card that failed yesterday is
 * being retried, and cutting a customer off in the middle of dunning loses
 * them over a bank's timing. The provider moves the subscription to a
 * terminal status if the retries fail, and that is when the plan goes.
 *
 * `created` is out, also on purpose. It is what a subscription is before the
 * first payment, and honouring it gave the paid plan to anyone who opened a
 * checkout and closed it.
 */
const PAID_STATUSES = new Set(['active', 'authenticated', 'trialing', 'past_due', 'pending']);

/** Plans that are sold, in ladder order. Classic is not among them (ADR-007). */
export const SELLABLE_PLANS: readonly SellablePlan[] = ['PRO', 'AGENCY'];

/** Plans that can be paid for, in ladder order: the sold ones and grandfathered Classic. */
export const PAID_PLANS: readonly PaidPlan[] = ['CLASSIC', 'PRO', 'AGENCY'];

/** Whether a stored plan name is one that is sold today: Pro or Agency. */
export function isSellablePlan(plan: string | null | undefined): plan is SellablePlan {
  return plan === 'PRO' || plan === 'AGENCY';
}

/** Whether a stored plan name is one that can be paid for: Classic (grandfathered), Pro or Agency. */
export function isPaidPlan(plan: string | null | undefined): plan is PaidPlan {
  return plan === 'CLASSIC' || isSellablePlan(plan);
}

/** Whether a resolved plan is the free Starter plan. */
export function isFreePlan(plan: string | null | undefined): boolean {
  return !isPaidPlan((plan ?? '').toUpperCase());
}

function parseDate(value: Date | string | null | undefined): Date | null {
  if (value === null || value === undefined) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * The plan an account is actually entitled to use.
 *
 * The one place this is decided, because the stored `plan` column alone says
 * what was asked for, not what was paid for. Callers pass the **account's**
 * subscription (ADR-006), never a brand's.
 *
 * - A paid or dunning status grants the plan (grandfathered Classic included).
 * - A cancelled subscription keeps its plan until the period it paid for ends.
 * - A trial carrying `trialEndsAt` keeps its plan until then.
 * - Anything else — no subscription, Starter, unpaid, halted, expired, the
 *   retired LAPSED value or an unknown plan name — is the free STARTER plan
 *   (ADR-007). Nothing resolves to a read-only state any more.
 */
export function effectivePlan(sub: SubscriptionState | null | undefined, now: Date = new Date()): PlanName {
  const plan = sub?.plan;
  if (!isPaidPlan(plan)) return FREE_PLAN;

  // No status recorded means a row written before statuses mattered, or by an
  // admin override: both are treated as paid.
  const status = (sub?.status ?? 'active').toLowerCase();

  if (status === 'trialing') {
    const trialEnd = parseDate(sub?.trialEndsAt);
    if (trialEnd) return trialEnd > now ? plan : FREE_PLAN;
    // A provider-side trial without our end date: paid, as before.
    return plan;
  }

  if (PAID_STATUSES.has(status)) return plan;

  if (status === 'cancelled' || status === 'canceled') {
    const end = parseDate(sub?.currentPeriodEnd);
    if (end && end > now) return plan;
  }
  return FREE_PLAN;
}

/**
 * When an account that pays for a plan now will fall back to Starter, if
 * nothing changes: the end of the period a cancelled subscription paid for
 * (or of a provider-side trial). Null when it renews, or when it is on
 * Starter already.
 */
export function planEndsAt(sub: SubscriptionState | null | undefined, now: Date = new Date()): Date | null {
  if (effectivePlan(sub, now) === FREE_PLAN) return null;
  const status = (sub?.status ?? 'active').toLowerCase();
  if (status === 'trialing') return parseDate(sub?.trialEndsAt);
  if (status === 'cancelled' || status === 'canceled') return parseDate(sub?.currentPeriodEnd);
  return null;
}

/**
 * Whether a subscription is on a timed plan right now: status `trialing`
 * with an end date still ahead. No sign-up gets one (ADR-007); an admin can
 * comp a plan until a date, and the admin console shows it as "trial".
 */
export function isTrialing(sub: SubscriptionState | null | undefined, now: Date = new Date()): boolean {
  if (!sub || (sub.status ?? '').toLowerCase() !== 'trialing') return false;
  const end = parseDate(sub.trialEndsAt);
  return end !== null && end > now;
}

/**
 * Plans in ladder order for a price list, so a UI never has to hard-code the
 * sequence: Starter (free) is shown beside it, Classic never is.
 */
export const PLAN_ORDER: readonly SellablePlan[] = SELLABLE_PLANS;

/**
 * Rank of each plan for "at least this plan" checks. Starter ranks below
 * every paid plan. Classic and Pro rank apart (Pro holds more brands) even
 * though their features match; feature gates should therefore ask for
 * 'classic', which a grandfathered Classic, Pro and Agency all pass.
 */
export const PLAN_LEVEL: Readonly<Record<PlanName, number>> = {
  STARTER: 0,
  CLASSIC: 1,
  PRO: 2,
  AGENCY: 3,
};

/**
 * What one plan costs in one currency.
 *
 * Deliberately total: every paid plan has a price in every billing currency,
 * so there is no "price missing" branch for a caller to get wrong. Classic's
 * is what a grandfathered subscriber is billed, never an offer.
 */
export function priceFor(plan: PaidPlan, currency: BillingCurrency): PlanPrice {
  return PLAN_PRICES[plan][currency];
}

/** What one extra Agency brand costs a month, in one currency. */
export function extraBrandMonthlyPrice(currency: BillingCurrency): number {
  return AGENCY_EXTRA_BRAND_PRICE[currency].monthly;
}

/** What one extra Agency brand costs, monthly and yearly, in one currency. */
export function extraBrandPrice(currency: BillingCurrency): PlanPrice {
  return AGENCY_EXTRA_BRAND_PRICE[currency];
}

/**
 * How many brands an account on `plan` may hold, counting the Agency extra
 * brands it pays for. Starter 1, Classic 1, Pro 3, Agency 5 + extras.
 */
export function brandLimitFor(plan: PlanName, extraBrands = 0): number {
  const included = PLAN_FEATURES[plan].maxWorkspaces;
  if (!PLAN_FEATURES[plan].extraBrandsPurchasable) return included;
  return included + Math.max(0, Math.floor(extraBrands));
}

/**
 * The cheapest plan whose *included* brands hold `brands`: Starter (free)
 * for one, Pro for two or three, Agency for four or more. Classic is never
 * the answer: it is not sold.
 */
export function cheapestPlanForBrands(brands: number): typeof FREE_PLAN | SellablePlan {
  if (brands <= PLAN_FEATURES.STARTER.maxWorkspaces) return FREE_PLAN;
  if (brands <= PLAN_FEATURES.PRO.maxWorkspaces) return 'PRO';
  return 'AGENCY';
}

/**
 * Whether a plan lets the agent send a reply without a person reading it
 * first. A function rather than a field read at each of the several call
 * sites that ask.
 */
export function canAutoSend(plan: PlanName): boolean {
  return PLAN_FEATURES[plan].aiRepliesAutoSent > 0;
}

/**
 * Razorpay settles in rupees, so that is what a card is charged today
 * whatever the buyer sees. Named rather than inlined, because every use of it
 * is a place that has to change when a European provider goes in front.
 * See docs/architecture/selling-outside-india.md.
 */
export const RAZORPAY_CURRENCY: BillingCurrency = 'INR';

/** GST on rupee charges. Razorpay is not a merchant of record, so UniPostudio charges it. */
export const INDIA_GST_PERCENT = 18;

/**
 * Whether a plan's rupee price in `PLAN_PRICES` already includes GST.
 *
 * Pro, Agency and the Agency extra brand: yes (owner's decision, ADR-007) —
 * ₹999 is what the customer pays. Grandfathered Classic: no — it keeps the
 * amount it was sold at, ₹499 with 18% GST added on top.
 */
export function inrPriceIncludesGst(plan: PaidPlan): boolean {
  return plan !== 'CLASSIC';
}

/**
 * A rupee price with GST added on top, rounded to the rupee. Only for prices
 * that exclude GST: grandfathered Classic and the credit packs.
 */
export function withIndiaGst(amountInr: number): number {
  return Math.round((amountInr * (100 + INDIA_GST_PERCENT)) / 100);
}

/** A GST-inclusive rupee amount split into what was earned and the GST inside it. */
export interface IndiaGstSplit {
  /** The whole amount charged, GST included. */
  total: number;
  /** total / 1.18, rounded to the paisa. */
  base: number;
  /** total − base: the GST inside the amount. */
  gst: number;
}

/**
 * Splits a GST-inclusive rupee amount: `base = round(total / 1.18, 2)`,
 * `gst = total − base` (ADR-007). Worked in paise so that ₹999 is exactly
 * ₹846.61 + ₹152.39, with no floating-point remainder.
 */
export function indiaGstSplit(totalInr: number): IndiaGstSplit {
  const totalPaise = Math.round(totalInr * 100);
  const basePaise = Math.round((totalPaise * 100) / (100 + INDIA_GST_PERCENT));
  return { total: totalPaise / 100, base: basePaise / 100, gst: (totalPaise - basePaise) / 100 };
}

/**
 * What Razorpay charges for a plan's rupee price, in rupees: the price as it
 * is for a GST-inclusive plan, with GST added for grandfathered Classic.
 */
export function rupeeChargeFor(plan: PaidPlan, amountInr: number): number {
  return inrPriceIncludesGst(plan) ? amountInr : withIndiaGst(amountInr);
}
